import fc from "@/firebaseConfig";
import firebase from "firebase/app";

export default {
  namespaced: true,
  state: {
    currentUploadLog: null,
    uploadHistory: [],
    loading: false,
    unsubscribeUploadHistory: null,
  },
  mutations: {
    SET_CURRENT_UPLOAD_LOG(state, log) {
      state.currentUploadLog = log;
    },
    SET_UPLOAD_HISTORY(state, history) {
      state.uploadHistory = history;
    },
    SET_LOADING(state, loading) {
      state.loading = loading;
    },
    UPDATE_UPLOAD_LOG(state, updates) {
      if (state.currentUploadLog) {
        state.currentUploadLog = {
          ...state.currentUploadLog,
          ...updates,
        };
      }
    },
    SET_UNSUBSCRIBE_UPLOAD_HISTORY(state, unsubscribe) {
      state.unsubscribeUploadHistory = unsubscribe;
    },
    CLEAR_UNSUBSCRIBE_UPLOAD_HISTORY(state) {
      state.unsubscribeUploadHistory = null;
    },
  },
  actions: {
    // สร้าง upload log ใหม่
    async createUploadLog({ commit, rootState }, { companyID, fileInfo, templateType }) {
      try {
        const user = firebase.auth().currentUser;
        const now = new Date();
        const uploadLog = {
          companyID,
          uploadedBy: user ? user.email : 'Unknown',
          uploadedByUID: user ? user.uid : null,
          uploadedAt: now, // ใช้ Date object สำหรับ client-side
          templateType, // 'template-1' or 'template-2'
          fileInfo: {
            name: fileInfo.name,
            size: fileInfo.size,
            sizeFormatted: formatFileSize(fileInfo.size),
            type: fileInfo.type,
            encoding: fileInfo.encoding,
            originalEncoding: fileInfo.originalEncoding,
          },
          status: 'processing', // processing, completed, failed
          totalRecords: 0,
          successCount: 0,
          failureCount: 0,
          failedRecords: [],
          errors: [],
          duration: 0,
        };

        commit('SET_CURRENT_UPLOAD_LOG', uploadLog);
        return uploadLog;
      } catch (error) {
        console.error('Error creating upload log:', error);
        throw error;
      }
    },

    // อัพเดท upload log ระหว่างการประมวลผล
    updateUploadLog({ commit }, updates) {
      commit('UPDATE_UPLOAD_LOG', updates);
    },

    // บันทึก upload log ลง Firestore
    async saveUploadLog({ state, commit }, { companyID }) {
      try {
        commit('SET_LOADING', true);
        
        if (!state.currentUploadLog) {
          throw new Error('No upload log to save');
        }

        const logData = {
          ...state.currentUploadLog,
          uploadedAt: firebase.firestore.FieldValue.serverTimestamp(),
          updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        };

        const docRef = await fc.db
          .collection('Companies')
          .doc(companyID)
          .collection('uploadLogs')
          .add(logData);

        console.log('✅ Upload log saved with ID:', docRef.id);
        
        commit('UPDATE_UPLOAD_LOG', { id: docRef.id });
        commit('SET_LOADING', false);
        
        return docRef.id;
      } catch (error) {
        console.error('❌ Error saving upload log:', error);
        commit('SET_LOADING', false);
        throw error;
      }
    },

    // ดึงประวัติการอัปโหลด (แบบเรียลไทม์)
    subscribeToUploadHistory({ commit, state }, { companyID, limit = 50 }) {
      try {
        // ยกเลิก listener เก่า (ถ้ามี)
        if (state.unsubscribeUploadHistory) {
          state.unsubscribeUploadHistory();
          commit('CLEAR_UNSUBSCRIBE_UPLOAD_HISTORY');
        }

        commit('SET_LOADING', true);

        // สร้าง real-time listener
        const unsubscribe = fc.db
          .collection('Companies')
          .doc(companyID)
          .collection('uploadLogs')
          .orderBy('uploadedAt', 'desc')
          .limit(limit)
          .onSnapshot(
            (snapshot) => {
              const history = [];
              snapshot.forEach((doc) => {
                history.push({
                  id: doc.id,
                  ...doc.data(),
                });
              });

              commit('SET_UPLOAD_HISTORY', history);
              commit('SET_LOADING', false);
              console.log('✅ Upload history updated (real-time):', history.length, 'items');
            },
            (error) => {
              console.error('❌ Real-time listener error:', error);
              commit('SET_LOADING', false);
            }
          );

        // เก็บ unsubscribe function
        commit('SET_UNSUBSCRIBE_UPLOAD_HISTORY', unsubscribe);

        return unsubscribe;
      } catch (error) {
        console.error('❌ Error setting up real-time listener:', error);
        commit('SET_LOADING', false);
        throw error;
      }
    },

    // ยกเลิก real-time listener
    unsubscribeFromUploadHistory({ commit, state }) {
      if (state.unsubscribeUploadHistory) {
        state.unsubscribeUploadHistory();
        commit('CLEAR_UNSUBSCRIBE_UPLOAD_HISTORY');
        console.log('✅ Unsubscribed from upload history');
      }
    },

    // ดึงประวัติการอัปโหลด (แบบครั้งเดียว - สำหรับ backward compatibility)
    async fetchUploadHistory({ commit }, { companyID, limit = 50 }) {
      try {
        commit('SET_LOADING', true);

        const snapshot = await fc.db
          .collection('Companies')
          .doc(companyID)
          .collection('uploadLogs')
          .orderBy('uploadedAt', 'desc')
          .limit(limit)
          .get();

        const history = [];
        snapshot.forEach((doc) => {
          history.push({
            id: doc.id,
            ...doc.data(),
          });
        });

        commit('SET_UPLOAD_HISTORY', history);
        commit('SET_LOADING', false);

        return history;
      } catch (error) {
        console.error('❌ Error fetching upload history:', error);
        commit('SET_LOADING', false);
        throw error;
      }
    },

    // ดึง upload log เฉพาะ
    async fetchUploadLogById({ commit }, { companyID, logId }) {
      try {
        const doc = await fc.db
          .collection('Companies')
          .doc(companyID)
          .collection('uploadLogs')
          .doc(logId)
          .get();

        if (doc.exists) {
          return {
            id: doc.id,
            ...doc.data(),
          };
        }
        return null;
      } catch (error) {
        console.error('❌ Error fetching upload log:', error);
        throw error;
      }
    },

    // ล้าง current upload log
    clearCurrentUploadLog({ commit }) {
      commit('SET_CURRENT_UPLOAD_LOG', null);
    },
  },
  getters: {
    currentUploadLog: (state) => state.currentUploadLog,
    uploadHistory: (state) => state.uploadHistory,
    loading: (state) => state.loading,
    hasFailures: (state) => state.currentUploadLog?.failureCount > 0,
    successRate: (state) => {
      if (!state.currentUploadLog || state.currentUploadLog.totalRecords === 0) {
        return 0;
      }
      return (
        (state.currentUploadLog.successCount / state.currentUploadLog.totalRecords) * 100
      ).toFixed(2);
    },
  },
};

// Helper function
function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
}

